/* eslint-disable max-len */
import { SwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { Component } from 'react';
import { withHousehold } from 'Context/HouseholdContext';
import { NotificationManager } from 'react-notifications';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';
import PropTypes from 'prop-types';
import { sentenceCase } from 'Helpers/Formats';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { Button, withStyles } from '@material-ui/core';
import AddNoteModal from './AddNoteModal';
import EditNoteModal from './EditNoteModal';
import Note from './Note';
import './index.css';

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const listLength = list.length;
  if (listLength <= 1) {
    return null;
  }

  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  let newWeight;
  if (endIndex === 0) {
    newWeight = result[1].attributes.weight / 2.0;
  } else if (endIndex === listLength - 1) {
    newWeight = result[endIndex - 1].attributes.weight + 1.0;
  } else {
    newWeight = (result[endIndex - 1].attributes.weight
        + result[endIndex + 1].attributes.weight)
      / 2.0;
  }
  result[endIndex].attributes.weight = newWeight;

  return result;
};

const typeMap = {
  recommendation: {
    name: 'Recommendation',
  },
  footnote: {
    name: 'Footnote',
  },
  strategy: {
    name: 'Strategy',
  },
};

class NoteSection extends Component {
  constructor(props) {
    super(props);
    const { sectionNotes } = this.props;
    this.state = {
      showAddModal: false,
      showEditModal: false,
      showForeignAddressModal: false,
      notes: sectionNotes,
    };

    this.toggleModal = this.toggleModal.bind(this);
    this.getMaxWeight = this.getMaxWeight.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.handleNoteClick = this.handleNoteClick.bind(this);
    this.updateNote = this.updateNote.bind(this);
    this.deleteNote = this.deleteNote.bind(this);
    this.appendNote = this.appendNote.bind(this);
  }

  onDragEnd(result) {
    const { notes } = this.state;
    const { destination, source } = result;
    if (!destination) {
      return;
    }

    const newNotes = reorder(notes, source.index, destination.index);
    this.setState({ notes: newNotes }, () => this.handleUpdate(destination.index));
  }

  onUpdateFinish() {
    const { showEditModal, type } = this.props;
    if (showEditModal) {
      this.toggleModal('showEditModal');
    }
    NotificationManager.success(`${typeMap[type].name} order saved.`);
  }

  getMaxWeight() {
    const { type } = this.props;
    const { notes } = this.state;
    const noteList = notes.filter(
      n => n.attributes.type.substring(0, 7) === type.substring(0, 7),
    );
    const greaterWeight = (prev, cur) => (prev.attributes.weight > cur.attributes.weight ? prev : cur);
    return noteList.length > 0
      ? noteList.reduce(greaterWeight).attributes.weight
      : 0;
  }

  toggleModal(type) {
    const { [type]: showModal } = this.state;
    this.setState({
      [type]: !showModal,
    });
  }

  handleNoteClick(note) {
    this.toggleModal('showEditModal');
    this.setState({
      selectedNote: note,
    });
  }

  handleUpdate(index) {
    const { notes } = this.state;
    const note = notes[index];
    const { householdId, sectionId, type } = this.props;
    const { updateQuestionnaireSectionNote: updateFunc } = this.context;
    const requestBody = {
      type: 'QuestionnaireSectionNote',
      attributes: {
        content: note.attributes.content,
        weight: note.attributes.weight,
        type: note.attributes.type,
      },
    };
    updateFunc(householdId, sectionId, type, note.id, requestBody).then(this.onUpdateFinish());
  }

  appendNote(data) {
    const newNote = data.data;
    const { notes } = this.state;
    this.setState({ notes: [...notes, newNote] });
  }

  deleteNote(data) {
    const newNote = data.data;
    const { notes } = this.state;
    const updatedNotes = notes.filter(n => n.id !== newNote.id);
    this.setState({ notes: updatedNotes });
  }

  updateNote(data) {
    const newNote = data.data;
    const { notes } = this.state;
    const newNotes = [...notes];
    const index = newNotes.findIndex(n => n.id === newNote.id);
    if (index < 0) {
      return;
    }
    newNotes[index] = newNote;
    this.setState({ notes: newNotes });
  }

  renderNotes() {
    const { notes } = this.state;
    if (!notes) {
      return null;
    }
    notes.sort((a, b) => a.attributes.weight - b.attributes.weight);
    return notes.map((n, i) => (
      <Note note={n} key={n.id} index={i} onClick={this.handleNoteClick} />
    ));
  }

  render() {
    const {
      householdId,
      type,
      sectionId,
      classes,
    } = this.props;
    const {
      showAddModal,
      showEditModal,
      showForeignAddressModal,
      selectedNote,
    } = this.state;
    const { updateQuestionnaireSectionNote: updateFunc } = this.context;
    return (
      <div className={`note-section ${type}`}>
        <div className="section-header">{sentenceCase(type)}</div>
        <div
          className={`add-note ${type}`}
          onClick={() => this.toggleModal('showAddModal')}
          role="presentation"
        >
          <FontAwesome icon="plus-circle" fontSize="14px" />
          {`Add ${typeMap[type].name}`}
        </div>
        {/* TODO: This should be broken out into a reusable component */}
        <DragDropContext onDragEnd={this.onDragEnd}>
          <Droppable droppableId={`droppable-${type}`}>
            {provided => (
              <div ref={provided.innerRef}>{this.renderNotes()}</div>
            )}
          </Droppable>
        </DragDropContext>
        <AddNoteModal
          sectionId={sectionId}
          isOpen={showAddModal}
          type={type}
          onClose={() => this.toggleModal('showAddModal')}
          maxWeightFunc={this.getMaxWeight}
          onDataUpdate={this.appendNote}
        />
        <EditNoteModal
          sectionId={sectionId}
          isOpen={showEditModal}
          type={type}
          onClose={() => this.toggleModal('showEditModal')}
          selectedNote={selectedNote}
          updateNote={(noteId, note) => updateFunc(householdId, sectionId, type, noteId, note)}
          onNoteUpdate={this.updateNote}
          onNoteDelete={this.deleteNote}
        />
        {(sectionId === 'contactInfo' || sectionId === 'significantOtherContactInfo') && type === 'footnote' && (
          <>
            <AddNoteModal
              sectionId={sectionId}
              isOpen={showForeignAddressModal}
              type={type}
              toggle={() => this.toggleModal('showForeignAddressModal')}
              maxWeightFunc={this.getMaxWeight}
              onDataUpdate={this.appendNote}
              content={sectionId === 'contactInfo' ? 'FOREIGN ADDRESS: ' : 'SIGNIFICANT OTHER FOREIGN ADDRESS: '}
            />
            <div className={classes.foreignAddr}>
              <Button variant="contained" color="secondary" onClick={() => this.toggleModal('showForeignAddressModal')}>Add Foreign Address</Button>
            </div>
          </>
        )}
      </div>
    );
  }
}

NoteSection.contextType = SwaggerlessContext;

NoteSection.propTypes = {
  householdId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  sectionId: PropTypes.string.isRequired,
  sectionNotes: PropTypes.array,
  showEditModal: PropTypes.bool,
  classes: PropTypes.object,
};

NoteSection.defaultProps = {
  showEditModal: false,
  sectionNotes: [],
  classes: {},
};

const styles = {
  foreignAddr: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '10px',
  },
};

export default withHousehold(withStyles(styles)(NoteSection));
